import {inject, injectable} from 'inversify';
import {map, Observable} from 'rxjs';
import {
  Employee,
  EmployeeResponse,
  EmployeesListResponse,
  ManagementStaff,
  ManagementStaffResponse,
} from '@innowise-group/core';
import {GlobalContainerTypes} from '../../types';
import {
  EmployeesApi,
  EmployeesBasicParams,
  EmployeesSearchParams,
  EmployeesWithoutBotParams,
} from './employees-api.types';
import {Http} from '../http';
import {EmployeesNormalization, ExtendedEmployee} from '../employees-normalization';
import {AxiosRequestConfig} from 'axios';

@injectable()
class EmployeesApiService implements EmployeesApi {
  public static readonly type = GlobalContainerTypes.EmployeesApi;

  @inject('Http') private http: Http;
  @inject(GlobalContainerTypes.EmployeesNormalization) private employeesNormalization: EmployeesNormalization;

  public getEmployees(params: EmployeesBasicParams): Observable<{employees: Employee[]; count: number}> {
    const urlParams = this.createUrlSearchParams(params);
    return this.http.GET<EmployeesListResponse>(`/employees/?${urlParams}`).pipe(
      map(({results, count}) => ({
        count,
        employees: results.map(this.employeesNormalization.normalizeEmployeeFromApi),
      })),
    );
  }

  public getEmployeeById(id: string): Observable<ExtendedEmployee> {
    return this.http.GET<EmployeeResponse>(`/employees/${id}/`).pipe(
      map(({active_requests, ...data}) => {
        const activeRequestWorkspaces = this.employeesNormalization.transformWorkspaces(active_requests);

        return {
          ...this.employeesNormalization.normalizeEmployeeFromApi(data),
          active_requests: this.employeesNormalization.normalizeEmployeeWorkspace(activeRequestWorkspaces),
        };
      }),
    );
  }

  public getEmployeeByHrmId(id: string): Observable<Employee> {
    return this.http
      .GET<EmployeesListResponse>(`/employees/?hrm_id=${id}`)
      .pipe(map(({results}) => results.length && this.employeesNormalization.normalizeEmployeeFromApi(results[0])));
  }

  public getManagementStaffList(
    locationId?: string,
    officeId?: string,
    config?: AxiosRequestConfig,
  ): Observable<ManagementStaff[]> {
    const params = new URLSearchParams('');
    if (locationId && !officeId) {
      params.append('location_id', locationId);
    }
    if (locationId && officeId) {
      params.append('office_id', officeId);
      params.append('location_id', locationId);
    }
    return this.http
      .GET<ManagementStaffResponse[]>(`/employees/managers/?${params}`, config)
      .pipe(map((list) => list.map(this.employeesNormalization.normalizeManagementStaffFromApi)));
  }

  public getEmployeesWithoutSeats(params: EmployeesSearchParams): Observable<{employees: Employee[]; count: number}> {
    const urlParams = this.createUrlSearchParams(params);
    return this.http.GET<EmployeesListResponse>(`/employees/loose-employees/?${urlParams}`).pipe(
      map(({results, count}) => ({
        count,
        employees: results.map(this.employeesNormalization.normalizeEmployeeFromApi),
      })),
    );
  }

  public getTotalEmployeesCount(): Observable<number> {
    return this.http.GET<{count: number}>(`employees/count/`).pipe(map(({count}) => count));
  }

  public getEmployeesCountWithoutBot(): Observable<number> {
    return this.http.GET<{count: number}>(`employees/bot-count/`).pipe(map(({count}) => count));
  }

  public getEmployeesWithoutBot(params: EmployeesWithoutBotParams): Observable<{employees: Employee[]; count: number}> {
    const urlParams = this.createUrlSearchParams(params);
    return this.http.GET<EmployeesListResponse>(`employees/bot-not-installed/?${urlParams}`).pipe(
      map(({results, count}) => ({
        count,
        employees: results.map(this.employeesNormalization.normalizeEmployeeFromApi),
      })),
    );
  }

  public getEmployeesCountWithoutSeats(): Observable<number> {
    return this.http.GET<{count: number}>(`/employees/loose-count/`).pipe(map(({count}) => count));
  }

  private createUrlSearchParams(params: EmployeesSearchParams): URLSearchParams {
    const urlParams = new URLSearchParams('');

    params?.name && urlParams.append('preferred_name', params.name);

    if (params?.actualOfficeId) {
      urlParams.append('actual_office_id', params.actualOfficeId);
    } else if (params?.jiraOfficeId) {
      urlParams.append('jira_office_id', params.jiraOfficeId);
    }

    if (params?.count && params?.page) {
      urlParams.append('limit', String(params.count));
      urlParams.append('offset', String(params.count * (params.page - 1)));
    }

    return urlParams;
  }
}

export default EmployeesApiService;
